import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Project, ProjectDTO, ProjectStub } from '../models/project-model';
import { MemberRoleProject } from '../models/avatar-model';
import { DocumentModel, DocumentStub, DocumentCheckAggregate } from '../models/document-model';
import { API_ENDPOINTS } from '../common/api/api-endpoints';
import { AvatarService } from '../users/avatar';
import { CurrentUserService } from '../../services/current-user';

/**
 * ProjectService
 *
 * Verantwortlichkeiten:
 * - API-Kommunikation für Projekte
 * - getProjects() gibt vorerst ein leeres Observable zurück,
 *   bis User-basiertes Laden implementiert ist.
 */
@Injectable({
  providedIn: 'root',
})
export class ProjectService {
  private readonly http = inject(HttpClient);
  private readonly avatarService = inject(AvatarService);
  private readonly currentUserService = inject(CurrentUserService);

  /**
   * Reaktives Signal – lädt Projekte des aktuell eingeloggten Users.
   * Mock-Modus: Interceptor liefert alle Projekte via GET /user/{oid}/member.
   * API-Modus: echter HTTP-Call auf Backend.
   */
  readonly projects = toSignal(
    toObservable(this.currentUserService.currentUserOid).pipe(
      switchMap(oid =>
        oid
          ? this.avatarService.getMembersByOid(oid).pipe(
              map(members => members.map(m => m.project as ProjectDTO))
            )
          : of([] as ProjectDTO[])
      )
    ),
    { initialValue: [] as ProjectDTO[] }
  );

  mapProject(dto: ProjectDTO): Project {
    return { ...dto };
  }

  // ===== Backend API Methoden =====

  /** POST /project */
  createProject(stub: ProjectStub): Observable<Project> {
    return this.http.post<Project>(API_ENDPOINTS.PROJECT, stub);
  }

  /** GET /project/{projectCode} */
  getProjectByCodeApi(projectCode: string): Observable<Project> {
    return this.http.get<Project>(`${API_ENDPOINTS.PROJECT}/${projectCode}`);
  }

  /** GET /project/{projectCode}/member */
  getProjectMembersApi(projectCode: string): Observable<MemberRoleProject[]> {
    return this.http.get<MemberRoleProject[]>(`${API_ENDPOINTS.PROJECT}/${projectCode}/member`);
  }

  /** GET /project/{projectCode}/document */
  getDocumentsByProjectApi(projectCode: string): Observable<DocumentCheckAggregate[]> {
    return this.http.get<DocumentCheckAggregate[]>(`${API_ENDPOINTS.PROJECT}/${projectCode}/document`);
  }

  /** GET /project/{projectCode}/document/{id} */
  getDocumentByProjectApi(projectCode: string, id: string): Observable<DocumentCheckAggregate> {
    return this.http.get<DocumentCheckAggregate>(`${API_ENDPOINTS.PROJECT}/${projectCode}/document/${id}`);
  }

  /** POST /project/{projectCode}/document → 201 */
  createDocumentForProjectApi(projectCode: string, payload: DocumentStub): Observable<DocumentModel> {
    return this.http.post<DocumentModel>(`${API_ENDPOINTS.PROJECT}/${projectCode}/document`, payload);
  }

}
